/* eslint-disable no-console */
import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { getCurrentUser, fetchUserAttributes, fetchAuthSession } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { client } from '../../lib/AmplifyClient';

// --- Types ---
interface UserState {
  isAuthenticated: boolean;
  isLoading: boolean;
  sub: string | null;
  email: string | null;
  firstName: string | null;
}

const INITIAL_STATE: UserState = {
  isAuthenticated: false,
  isLoading: true,
  sub: null,
  email: null,
  firstName: null,
};

// --- Context ---
const UserContext = createContext<{
  user: UserState;
  refreshUser: () => Promise<void>;
  clearUser: () => void;
  updateLocal: (patch: Partial<UserState>) => void;
}>({
  user: INITIAL_STATE,
  refreshUser: async () => {},
  clearUser: () => {},
  updateLocal: () => {},
});

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<UserState>(INITIAL_STATE);
  const syncing = useRef(false);

  const clearUser = useCallback(() => setUser({ ...INITIAL_STATE, isLoading: false }), []);
  
  const updateLocal = useCallback((patch: Partial<UserState>) => {
    setUser((prev) => ({ ...prev, ...patch }));
  }, []);

  const refreshUser = useCallback(async () => {
    if (syncing.current) return;
    syncing.current = true;
    setUser((u) => ({ ...u, isLoading: true }));

    try {
      // Small delay to ensure Amplify is fully initialized
      // (especially important on initial page load)
      await new Promise(resolve => setTimeout(resolve, 100));

      // Step 1: Check if we have a valid session (more reliable than getCurrentUser)
      const session = await fetchAuthSession();
      console.log('[UserContext] Session check:', { 
        hasIdToken: !!session?.tokens?.idToken,
        hasAccessToken: !!session?.tokens?.accessToken 
      });

      if (!session?.tokens?.idToken && !session?.tokens?.accessToken) {
        console.log('[UserContext] No valid tokens found');
        clearUser();
        return;
      }

      // Step 2: Get user details
      const authUser = await getCurrentUser();
      console.log('[UserContext] Found authenticated user:', authUser.userId);

      // Step 3: Fetch user attributes
      const attrs = (await fetchUserAttributes()) as Record<string, string | undefined>;
      console.log('[UserContext] Fetched attributes:', { email: attrs.email, name: attrs.given_name });
      
      const email = attrs.email || authUser.signInDetails?.loginId;
      const sub = authUser.userId;
      const name = attrs.given_name || attrs.name || attrs['custom:firstName'];

      if (!sub || !email) {
        console.warn('[UserContext] Missing sub or email, clearing user');
        clearUser();
        return;
      }

      // Step 4: Sync with DB (don't let DB errors block auth state)
      let profile = null;
      try {
        profile = await syncUserProfile(sub, email, name);
        console.log('[UserContext] Profile synced:', profile?.emailLower);
      } catch (dbErr) {
        console.error('[UserContext] DB sync failed (non-fatal):', dbErr);
      }

      setUser({
        isAuthenticated: true,
        isLoading: false,
        sub,
        email,
        firstName: profile?.firstName ?? name ?? null,
      });
      console.log('[UserContext] User state updated - authenticated');
    } catch (e) {
      // Not authenticated or session check failed
      console.log('[UserContext] Authentication failed:', e);
      clearUser();
    } finally {
      syncing.current = false;
    }
  }, [clearUser]);

  useEffect(() => {
    refreshUser();
    return Hub.listen('auth', ({ payload: { event } }) => {
      if (['signedIn', 'tokenRefresh'].includes(event)) refreshUser();
      if (event === 'signedOut') clearUser();
    });
  }, [refreshUser, clearUser]);

  return (
    <UserContext.Provider value={{ user, refreshUser, clearUser, updateLocal }}>
      {children}
    </UserContext.Provider>
  );
};

// --- Hook (Maps state for backward compatibility) ---
export const useUserContext = () => {
  const { user, refreshUser, clearUser, updateLocal } = useContext(UserContext);
  return {
    ...user,
    userId: user.sub, // Alias sub as userId
    refreshUser,
    clearUser,
    setFirstName: (firstName: string | null) => updateLocal({ firstName }),
    setEmail: (email: string | null) => updateLocal({ email }),
    // Mock unused fields to prevent breaking changes if currently referenced
    isGuest: false,
    identityId: null,
    __uid: Date.now(), // Force effect dependencies if used elsewhere
  };
};

// --- Helper: Database Logic ---
async function syncUserProfile(sub: string, email: string, firstName?: string | null) {
  const emailLower = email.trim().toLowerCase();
  try {
    // 1. Try find by Sub, then Email
    const { data: bySub } = await client.models.Profile.list({ filter: { sub: { eq: sub } } });
    let profile = bySub[0];

    if (!profile) {
      const { data: byEmail } = await client.models.Profile.list({ filter: { emailLower: { eq: emailLower } } });
      profile = byEmail[0];
    }

    // 2. Create if missing
    if (!profile) {
      const { data } = await client.models.Profile.create({ sub, email, emailLower, firstName });
      return data;
    }

    // 3. Update if changed
    const needsUpdate = profile.email !== email || profile.firstName !== firstName || profile.sub !== sub;
    if (needsUpdate) {
      const { data } = await client.models.Profile.update({
        emailLower: profile.emailLower, // PK
        email,
        firstName,
        sub,
      });
      return data;
    }
    return profile;
  } catch (err) {
    console.error('Profile sync error:', err);
    return null;
  }
}
